import { CookieConstants, PERSISTED_AUTH_COOKIE_DAYS, StorageConstants } from '@linkie/shared';
import { CONSENT_CATEGORY, type ConsentSurface, CONSENT_SURFACE } from '@/consent/contract';
import type { DisclosureEntry, DisclosureInventory } from '@/consent/disclosures';

const LINKIE = 'Linkie';

const PERSISTED_COOKIE_DURATION = `Until you close the browser, or ${PERSISTED_AUTH_COOKIE_DAYS} days if you chose to stay signed in`;

const ANALYTICS_ENTRIES: DisclosureEntry[] = [];

const CLOUDFLARE = 'Cloudflare';

const CLOUDFLARE_ENTRIES: DisclosureEntry[] = [
  {
    name: '__cf_bm',
    provider: CLOUDFLARE,
    purpose: 'Tells human visitors apart from bots, so automated traffic can be blocked.',
    duration: '30 minutes',
    type: 'Cookie',
  },
  {
    name: 'cf_clearance',
    provider: CLOUDFLARE,
    purpose: 'Records that you passed a security check, so you are not challenged again on every page.',
    duration: 'Set by Cloudflare, typically 30 minutes',
    type: 'Cookie',
  },
];

const PUBLIC_PAGE: DisclosureInventory = {
  [CONSENT_CATEGORY.NECESSARY]: [...CLOUDFLARE_ENTRIES],
  [CONSENT_CATEGORY.FUNCTIONAL]: [
    {
      name: `${StorageConstants.USER_SUBSCRIPTION_PREFIX}…`,
      provider: LINKIE,
      purpose: 'Remembers that you already signed up with your email, so the form is not shown to you again.',
      duration: 'Until you clear your browser storage',
      type: 'Local storage',
    },
  ],
  [CONSENT_CATEGORY.ANALYTICS]: ANALYTICS_ENTRIES,
};

const APP: DisclosureInventory = {
  [CONSENT_CATEGORY.NECESSARY]: [
    ...CLOUDFLARE_ENTRIES,
    {
      name: CookieConstants.USER_ACCESS_TOKEN,
      provider: LINKIE,
      purpose: 'Keeps you signed in.',
      duration: PERSISTED_COOKIE_DURATION,
      type: 'Cookie',
    },
    {
      name: CookieConstants.PAGE_ID,
      provider: LINKIE,
      purpose: 'Tells the API which of your pages you are working on.',
      duration: 'Until you close the browser',
      type: 'Cookie',
    },
    {
      name: CookieConstants.NOLT_TOKEN,
      provider: LINKIE,
      purpose: 'Signs you in to the feedback board without a second login.',
      duration: PERSISTED_COOKIE_DURATION,
      type: 'Cookie',
    },
    {
      name: StorageConstants.IDENTITY,
      provider: LINKIE,
      purpose: 'Keeps your account details available between page loads.',
      duration: 'Until you sign out',
      type: 'IndexedDB',
    },
    {
      name: StorageConstants.PLAYGROUND_STORE,
      provider: LINKIE,
      purpose: 'Saves an unsaved page draft when you are not signed in, so you do not lose it.',
      duration: 'Until you clear your browser storage',
      type: 'IndexedDB',
    },
    {
      name: StorageConstants.HAS_CUSTOM_SOCIAL_IMAGE,
      provider: LINKIE,
      purpose: 'Remembers that you uploaded your own social preview image, so it is not overwritten.',
      duration: 'Until you clear your browser storage',
      type: 'Local storage',
    },
    {
      name: StorageConstants.EMAIL_INVITATION_TEXT,
      provider: LINKIE,
      purpose: 'Keeps the invitation message you were drafting so it survives a reload.',
      duration: 'Until you clear your browser storage',
      type: 'Local storage',
    },
    {
      name: `${StorageConstants.PUBLER_DISCOUNT_POPUP_DISMISSED}_…`,
      provider: LINKIE,
      purpose: 'Remembers that you dismissed the Publer discount offer, so it is not shown again.',
      duration: 'Until you clear your browser storage',
      type: 'Local storage',
    },
    {
      name: 'Kibo',
      provider: 'Kibo',
      purpose:
        'Runs the live chat widget, and passes it your name, email and avatar so support knows who they are talking to.',
      duration: 'Set by Kibo',
      type: 'Third-party',
    },
    {
      name: 'Nolt',
      provider: 'Nolt',
      purpose: 'Runs the feedback board widget.',
      duration: 'Set by Nolt',
      type: 'Third-party',
    },
  ],
};

export const disclosureInventoryFor = (surface: ConsentSurface): DisclosureInventory =>
  surface === CONSENT_SURFACE.PUBLIC_PAGE ? PUBLIC_PAGE : APP;

export const ALL_INVENTORIES: DisclosureInventory[] = [PUBLIC_PAGE, APP];
